import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Megaphone, Pin, Check, ChevronRight, Send, ClipboardList } from 'lucide-react';
import api from '../api';
import { Sheet, Seg, Avatar, plural } from './AdminUi';
import NotifySheet from './NotifySheet';

// Obavijesti → „Poslane”: ručno poslane obavijesti (zadatak / Oglasna ploča) s pročitanošću.
// Admin vidi sva slanja (čip „Moje” za svoja), voditelj grupe samo svoja. Backend čuva zadnjih 6 mjeseci.

const DAYS = ['ned', 'pon', 'uto', 'sri', 'čet', 'pet', 'sub'];
const hm = (d) => new Date(d).toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });
const dayDiff = (d) => {
  const a = new Date(); a.setHours(0, 0, 0, 0);
  const b = new Date(d); b.setHours(0, 0, 0, 0);
  return Math.round((a - b) / 86400000);
};
const dayKey = (d) => {
  const diff = dayDiff(d);
  if (diff === 0) return 'Danas';
  if (diff === 1) return 'Jučer';
  const x = new Date(d);
  return `${DAYS[x.getDay()]} ${x.getDate()}. ${x.getMonth() + 1}.`;
};
const whenText = (d) => {
  const diff = dayDiff(d);
  return `${diff === 0 ? 'danas' : diff === 1 ? 'jučer' : `${new Date(d).getDate()}. ${new Date(d).getMonth() + 1}.`} u ${hm(d)}`;
};
const refTitle = (s) => (s.kind === 'task' ? s.ref_title || 'Zadatak' : 'Oglasna ploča');

function ReadBadge({ s }) {
  if (s.total > 0 && s.read >= s.total) {
    return <span className="sn-all"><Check size={14} strokeWidth={3} /> svi</span>;
  }
  const pct = s.total ? Math.round((s.read / s.total) * 100) : 0;
  return (
    <>
      <span className="sn-rd tab">{s.read}/{s.total}</span>
      <span className="sn-bar"><i style={{ width: `${pct}%` }} /></span>
    </>
  );
}

function KindIcon({ kind }) {
  return kind === 'task'
    ? <span className="sn-ic task"><Megaphone size={16} /></span>
    : <span className="sn-ic ann"><Pin size={16} /></span>;
}

function SendDetail({ id, onClose, onOpenTask, onOpenBoard, onChanged }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(null);
  const [remind, setRemind] = useState(false);

  const load = useCallback(() => {
    api.get(`/notify/sends/${id}`).then(r => setD(r.data)).catch(err => setError(err.response?.data?.error || 'Greška pri dohvatu.'));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const rs = d?.recipients || [];
    return {
      unread: rs.filter(r => !r.read_at && !r.deleted),
      read: rs.filter(r => r.read_at),
      deleted: rs.filter(r => !r.read_at && r.deleted),
    };
  }, [d]);
  // Zadano: „Nisu” ako ih ima, inače „Pročitali”.
  const curTab = tab || (groups.unread.length ? 'unread' : groups.read.length ? 'read' : 'deleted');
  const notRead = [...groups.unread, ...groups.deleted];
  const refOk = d && (d.kind === 'task' ? d.task_exists : d.ann_exists);
  const total = d?.recipients.length || 0;
  const pctRead = total ? (groups.read.length / total) * 100 : 0;
  const pctDel = total ? (groups.deleted.length / total) * 100 : 0;

  const list = groups[curTab];
  const status = (r) => (r.read_at ? `pročitano ${whenText(r.read_at)}` : r.deleted ? 'obrisano nepročitano' : 'nije pročitano');

  return (
    <>
      <Sheet tall z={1200} onClose={onClose} swipeBody
        head={
          <div className="adm-sh">
            <div className="adm-main">
              <h3>{d ? refTitle(d) : 'Poslana obavijest'}</h3>
              {d && <p>{d.sender_name} · {whenText(d.created_at)} · {total} {plural(total, 'primatelj', 'primatelja', 'primatelja')}</p>}
            </div>
          </div>
        }
        footer={d && (
          <button type="button" className="adm-btn" disabled={!refOk || notRead.length === 0} onClick={() => setRemind(true)}>
            <Send size={16} />
            {!refOk ? (d.kind === 'task' ? 'Zadatak je obrisan' : 'Objava je obrisana')
              : notRead.length === 0 ? 'Svi su pročitali'
                : `Podsjeti one koji nisu pročitali (${notRead.length})`}
          </button>
        )}
      >
        {error && <p className="adm-err">{error}</p>}
        {!d && !error && <p className="adm-note">Učitavam…</p>}
        {d && <>
          <div className="adm-card">
            <div className="sn-msg">{d.message}</div>
            {d.audience && <div className="sn-aud">Poslano: {d.audience}</div>}
            {refOk && (
              <button type="button" className="adm-act" onClick={() => (d.kind === 'task' ? onOpenTask(d.task_id) : onOpenBoard())}>
                {d.kind === 'task' ? <ClipboardList size={18} /> : <Pin size={18} />}
                {d.kind === 'task' ? 'Otvori zadatak' : 'Otvori Oglasnu ploču'}
                <ChevronRight size={17} color="#b2bec3" style={{ marginLeft: 'auto' }} />
              </button>
            )}
          </div>

          <div className="adm-lbl">Pročitanost</div>
          <div className="adm-card">
            <div className="sn-big"><b className="tab">{groups.read.length}</b><span>od {total} pročitalo</span></div>
            <div className="sn-bigbar"><i style={{ width: `${pctRead}%` }} /><i className="del" style={{ width: `${pctDel}%` }} /></div>
          </div>
          <Seg
            value={curTab}
            onChange={setTab}
            options={[
              { value: 'unread', label: `Nisu · ${groups.unread.length}` },
              { value: 'read', label: `Pročitali · ${groups.read.length}` },
              { value: 'deleted', label: `Obrisali · ${groups.deleted.length}` },
            ]}
          />
          {list.length === 0 ? (
            <p className="adm-note" style={{ textAlign: 'center', padding: '8px 0' }}>
              {curTab === 'unread' ? 'Svi su pročitali ili obrisali.' : curTab === 'read' ? 'Još nitko nije pročitao.' : 'Nitko nije obrisao bez čitanja.'}
            </p>
          ) : (
            <div className="adm-card">
              {list.map(r => (
                <div key={r.email} className="adm-it" style={{ cursor: 'default' }}>
                  <Avatar name={r.full_name} size={32} />
                  <div className="adm-main"><div className="adm-t soft" style={{ fontSize: 15.5 }}>{r.full_name}</div></div>
                  <span className="sn-st">{status(r)}</span>
                </div>
              ))}
            </div>
          )}
          {groups.deleted.length > 0 && <p className="adm-note">„Obrisali” su obavijest maknuli prije nego što su je otvorili.</p>}
        </>}
      </Sheet>

      {remind && d && (
        <NotifySheet
          z={1300}
          title="Podsjeti nepročitane"
          subtitle={`${refTitle(d)} · poslano ${whenText(d.created_at)}`}
          sources={[{ key: 'unread', label: 'Nisu pročitali', people: notRead.map(r => ({ email: r.email, name: r.full_name, sub: r.deleted ? 'obrisao/la nepročitano' : null })) }]}
          defaultSrc={['unread']}
          templates={[
            { key: 'reminder', label: 'Podsjetnik', text: `Podsjetnik: ${d.message}`.slice(0, 500) },
            { key: 'same', label: 'Ista poruka', text: d.message },
          ]}
          emptyText="Nema kome poslati."
          note="Šalje se kao nova obavijest (in-app + push) i pojavljuje se kao novo slanje na popisu."
          onSend={async (emails, message) => {
            const url = d.kind === 'task' ? `/tasks/${d.task_id}/notify` : `/announcement/${d.announcement_id}/notify`;
            const res = await api.post(url, { emails, message, audience: 'Podsjetnik nepročitanima' });
            return res.data.sent;
          }}
          onSent={() => { setRemind(false); onChanged(); onClose(); }}
          onClose={() => setRemind(false)}
        />
      )}
    </>
  );
}

export default function SentNotifications({ user, onOpenTask, onOpenBoard }) {
  const isAdmin = user.role === 'admin';
  const [sends, setSends] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    api.get('/notify/sends').then(r => { setSends(r.data); setError(''); })
      .catch(err => setError(err.response?.data?.error || 'Greška pri dohvatu poslanih obavijesti.'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const FILTERS = [
    { key: 'all', label: 'Sve' },
    { key: 'task', label: 'Zadaci' },
    { key: 'announcement', label: 'Oglasna ploča' },
    { key: 'mine', label: 'Moje' },
  ];
  const shown = (sends || []).filter(s => filter === 'all' || (filter === 'mine' ? s.sender_email === user.email : s.kind === filter));
  const days = [];
  for (const s of shown) {
    const key = dayKey(s.created_at);
    const last = days[days.length - 1];
    if (last && last.key === key) last.items.push(s);
    else days.push({ key, items: [s] });
  }

  return (
    <>
      {isAdmin && (
        <div className="ntf-chips">
          {FILTERS.map(f => (
            <button type="button" key={f.key} className={filter === f.key ? 'on' : ''} onClick={() => setFilter(f.key)}>{f.label}</button>
          ))}
        </div>
      )}
      {!isAdmin && <p className="sn-note">Obavijesti koje si ti poslao/la uz zadatke.</p>}
      {error && <div className="ntf-err">{error}</div>}

      {sends && shown.length === 0 ? (
        <div className="ntf-empty">
          <Send size={34} color="#dfe6e9" />
          <div>
            {sends.length === 0
              ? (isAdmin ? 'Još nema poslanih obavijesti.' : 'Još nisi poslao/la nijednu obavijest.')
              : 'Nema poslanih obavijesti u ovoj skupini.'}
          </div>
          {sends.length === 0 && (
            <div className="sn-empty-sub">
              {isAdmin ? 'Na zadatku ili objavi otvori ⋯ → Pošalji obavijest.' : 'Na zadatku otvori ⋯ → Pošalji obavijest.'} Ovdje ćeš vidjeti tko ju je pročitao.
            </div>
          )}
        </div>
      ) : days.map(day => (
        <div key={day.key}>
          <div className="ntf-day">{day.key}</div>
          <div className="ntf-card">
            {day.items.map(s => (
              <button key={s.id} type="button" className="sn-row" onClick={() => setOpenId(s.id)}>
                <KindIcon kind={s.kind} />
                <span className="sn-tx">
                  <b>{refTitle(s)}</b>
                  <small className="sn-who">{s.sender_name}{s.audience ? ` → ${s.audience}` : ''} · {s.total}</small>
                  <small className="sn-msgline">{s.message}</small>
                </span>
                <span className="sn-side">
                  <span className="sn-tm tab">{hm(s.created_at)}</span>
                  <ReadBadge s={s} />
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
      {sends && sends.length > 0 && <div className="ntf-hint">Prikazuju se slanja iz zadnjih 6 mjeseci.</div>}

      {openId && (
        <SendDetail
          id={openId}
          onClose={() => setOpenId(null)}
          onOpenTask={(taskId) => { setOpenId(null); onOpenTask(taskId); }}
          onOpenBoard={() => { setOpenId(null); onOpenBoard && onOpenBoard(); }}
          onChanged={load}
        />
      )}
    </>
  );
}
